// src/features/health/components/reports/HealthReportPreview.tsx
// BP-03-04 Part 3: Health report preview component

import React from 'react';
import { format } from 'date-fns';
import { 
  FileText, 
  Calendar, 
  User, 
  Activity,
  Pill,
  TrendingUp,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Separator } from '@/components/ui/Separator';
import { Progress } from '@/components/ui/Progress';
import type { HealthReport, ReportSection } from '../../types/reportTypes';

interface HealthReportPreviewProps {
  report: HealthReport;
  className?: string;
}

export const HealthReportPreview: React.FC<HealthReportPreviewProps> = ({
  report,
  className
}) => {
  const renderSectionContent = (section: ReportSection) => {
    switch (section.type) {
      case 'overview':
        return <OverviewSection data={section.data} />;
      case 'medications':
        return <MedicationsSection data={section.data} />;
      case 'symptoms':
        return <SymptomsSection data={section.data} />;
      case 'vitals':
        return <VitalsSection data={section.data} />;
      case 'therapy':
        return <TherapySection data={section.data} />;
      case 'goals':
        return <GoalsSection data={section.data} />;
      default:
        return <GenericSection data={section.data} />;
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Report Header */}
      <div className="text-center pb-6 border-b">
        <h1 className="text-2xl font-display font-semibold text-navy">
          {report.title}
        </h1>
        <div className="flex items-center justify-center gap-4 mt-2 text-sm text-warm-gray">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {format(new Date(report.metadata.generatedAt), 'PPP')}
          </span>
          <span className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            {report.metadata.reportType}
          </span>
        </div>
        <p className="mt-2 text-sm text-warm-gray">
          Period: {format(new Date(report.metadata.dateRange.start), 'PP')} - {format(new Date(report.metadata.dateRange.end), 'PP')}
        </p>
      </div>

      {/* Executive Summary */}
      {report.summary && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-display">Executive Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {report.summary.keyMetrics.map((metric, idx) => (
                <div key={idx} className="text-center p-3 bg-cream rounded-lg">
                  <p className="text-2xl font-display font-semibold text-navy">
                    {metric.value}
                  </p>
                  <p className="text-xs text-warm-gray">{metric.label}</p>
                  {metric.trend && (
                    <Badge 
                      variant={metric.trend > 0 ? 'default' : 'destructive'}
                      className="mt-1 text-xs"
                    >
                      {metric.trend > 0 ? '+' : ''}{metric.trend}%
                    </Badge>
                  )}
                </div>
              ))}
            </div>
            
            {report.summary.highlights.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-navy">Key Highlights</h4>
                <ul className="space-y-1">
                  {report.summary.highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-sage mt-0.5 flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {report.summary.concerns.length > 0 && (
              <div className="space-y-2 mt-4">
                <h4 className="text-sm font-medium text-navy">Areas of Concern</h4>
                <ul className="space-y-1">
                  {report.summary.concerns.map((concern, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <AlertCircle className="w-4 h-4 text-terracotta mt-0.5 flex-shrink-0" />
                      <span>{concern}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Report Sections */}
      {report.sections.map((section, idx) => (
        <Card key={idx}>
          <CardHeader>
            <CardTitle className="text-lg font-display flex items-center gap-2">
              {getSectionIcon(section.type)}
              {section.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {renderSectionContent(section)}
          </CardContent>
        </Card>
      ))}

      {/* Footer */}
      <div className="text-center text-xs text-warm-gray pt-4 border-t">
        <p>Generated by BrightPath Health Tracking System</p>
        <p>Report ID: {report.id}</p>
      </div>
    </div>
  );
};

const getSectionIcon = (type: string) => {
  switch (type) {
    case 'overview':
      return <Activity className="w-5 h-5 text-sage" />;
    case 'medications':
      return <Pill className="w-5 h-5 text-blue-500" />;
    case 'symptoms':
      return <AlertCircle className="w-5 h-5 text-amber-500" />;
    case 'vitals':
      return <TrendingUp className="w-5 h-5 text-red-500" />;
    case 'therapy':
      return <User className="w-5 h-5 text-purple-500" />;
    case 'goals':
      return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    default:
      return <FileText className="w-5 h-5 text-gray-500" />;
  }
};

// Section Components
const OverviewSection: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-4">
    <p className="text-sm text-warm-gray">{data?.summary || 'Overview summary not available.'}</p>
    {data?.stats && (
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(data.stats).map(([key, value]) => (
          <div key={key} className="text-center p-2 bg-cream/50 rounded">
            <p className="text-lg font-semibold">{String(value)}</p>
            <p className="text-xs text-warm-gray capitalize">{key.replace(/_/g, ' ')}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

const MedicationsSection: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-4">
    {data?.adherenceRate !== undefined && (
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Overall Adherence</span>
          <span className="font-medium">{data.adherenceRate}%</span>
        </div>
        <Progress value={data.adherenceRate} className="h-2" />
      </div>
    )}
    {data?.medications && (
      <div className="space-y-2">
        {data.medications.map((med: any, idx: number) => (
          <div key={idx} className="flex items-center justify-between p-2 bg-cream/50 rounded">
            <div>
              <p className="font-medium text-sm">{med.name}</p>
              <p className="text-xs text-warm-gray">{med.dosage} - {med.frequency}</p>
            </div>
            <Badge variant={med.adherence >= 80 ? 'default' : 'destructive'}>
              {med.adherence}%
            </Badge>
          </div>
        ))}
      </div>
    )}
  </div>
);

const SymptomsSection: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-4">
    {data?.totalOccurrences !== undefined && (
      <p className="text-sm">
        Total symptom occurrences: <strong>{data.totalOccurrences}</strong>
      </p>
    )}
    {data?.byType && (
      <div className="space-y-2">
        {Object.entries(data.byType).map(([type, count]) => (
          <div key={type} className="flex items-center justify-between">
            <span className="text-sm capitalize">{type}</span>
            <Badge variant="outline">{String(count)}</Badge>
          </div>
        ))}
      </div>
    )}
  </div>
);

const VitalsSection: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-4">
    {data?.averages && (
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(data.averages).map(([key, value]) => (
          <div key={key} className="text-center p-3 bg-cream/50 rounded">
            <p className="text-lg font-semibold">{String(value)}</p>
            <p className="text-xs text-warm-gray capitalize">{key.replace(/_/g, ' ')}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

const TherapySection: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-4">
    {data?.sessions !== undefined && (
      <p className="text-sm">
        Total sessions: <strong>{data.sessions}</strong>
      </p>
    )}
    {data?.byType && (
      <div className="space-y-2">
        {Object.entries(data.byType).map(([type, count]) => (
          <div key={type} className="flex items-center justify-between p-2 bg-cream/50 rounded">
            <span className="text-sm">{type}</span>
            <span className="font-medium">{String(count)} sessions</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const GoalsSection: React.FC<{ data: any }> = ({ data }) => (
  <div className="space-y-4">
    {data?.goals && (
      <div className="space-y-3">
        {data.goals.map((goal: any, idx: number) => (
          <div key={idx} className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{goal.title}</span>
              <span className="font-medium">{goal.progress}%</span>
            </div>
            <Progress value={goal.progress} className="h-2" />
          </div>
        ))}
      </div>
    )}
  </div>
);

const GenericSection: React.FC<{ data: any }> = ({ data }) => (
  <div className="text-sm text-warm-gray">
    {data ? (
      <pre className="whitespace-pre-wrap font-sans">
        {JSON.stringify(data, null, 2)}
      </pre>
    ) : (
      <p>No data available for this section.</p>
    )}
  </div>
);

export default HealthReportPreview;

